import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, RefreshCw, Repeat, Search, Sparkles, X } from 'lucide-react'
import { useProvideFeedbackContext } from '../components/feedback/FeedbackContext'
import { Button } from '../components/ui/Button'
import { useToast } from '../components/ui/Toast'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { SkeletonList } from '../components/ui/Skeleton'
import { useTheme } from '../components/providers/ThemeProvider'
import { useBranding } from '../hooks/useBranding'
import { HabitService, type Habit, type HabitBoardCard } from '../services/habitService'
import { describeTaskCleanup, lifeAreaLabel } from '../utils/taskForm'
import { groupHabits, matchesQuery, summarize, summaryWords, type HabitGroupBy, type HabitSortBy, type SortDir } from '../utils/habitSections'
import { HabitRow } from '../components/habits/HabitRow'
import { TaskQuickAdd } from '../components/tasks/TaskQuickAdd'

/**
 * The habit list (Habits UX pass 2026-09-06), read the way a person reads
 * it — which are due today, are they done, how am I keeping up:
 *
 *   - Logo, one title, one summary line, two verbs — the task list's frame.
 *   - A quick-add row: a name is enough; it lands under "Needs a schedule"
 *     with the next step in view.
 *   - Sections by attention: Today · This week · Later · Needs a schedule ·
 *     Paused · Archived (collapsed). Goal / Life area / None one click away.
 *   - A row is a mark, a title, the run, one quiet line. Tap the mark to
 *     finish today (with Undo). The rest under ⋯.
 *   - Search instead of pagination; group, sort and direction remembered.
 */
const GROUP_PREF = 'habits.groupBy'
const SORT_PREF = 'habits.sortBy'
const DIR_PREF = 'habits.sortDir'
const pad = (n: number) => String(n).padStart(2, '0')
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }

function readPref<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key)
    return (allowed as readonly string[]).includes(v || '') ? (v as T) : fallback
  } catch { return fallback }
}
function writePref(key: string, value: string) {
  try { localStorage.setItem(key, value) } catch { /* per-viewer convenience only */ }
}

export const Habits: React.FC = () => {
  const { isDiary: diaryMode } = useTheme()
  const { assistant: personaName } = useBranding()
  const navigate = useNavigate()
  const toast = useToast()
  const [habits, setHabits] = useState<Habit[]>([])
  const [cards, setCards] = useState<Record<string, HabitBoardCard | undefined>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [boardIssue, setBoardIssue] = useState(false)
  const [query, setQuery] = useState('')
  const [groupBy, setGroupByState] = useState<HabitGroupBy>(() => readPref(GROUP_PREF, ['when', 'goal', 'area', 'none'] as const, 'when'))
  const [sortBy, setSortByState] = useState<HabitSortBy>(() => readPref(SORT_PREF, ['attention', 'streak', 'created', 'updated'] as const, 'attention'))
  const [sortDir, setSortDirState] = useState<SortDir>(() => readPref(DIR_PREF, ['asc', 'desc'] as const, 'desc'))
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [deleteTarget, setDeleteTarget] = useState<Habit | null>(null)
  const [today, setToday] = useState(todayKey)
  const habitService = useMemo(() => new HabitService(), [])

  useProvideFeedbackContext({ pageType: 'habits', extra: { groupBy, sortBy, sortDir, searching: Boolean(query) } })

  const setGroupBy = (g: HabitGroupBy) => { setGroupByState(g); writePref(GROUP_PREF, g) }
  const setSortBy = (s: HabitSortBy) => { setSortByState(s); writePref(SORT_PREF, s) }
  const setSortDir = (d: SortDir) => { setSortDirState(d); writePref(DIR_PREF, d) }

  const load = useCallback(async () => {
    const day = todayKey()
    setToday(day)
    try {
      setError(null)
      // include=all: rationing parks fresh habits; an active-only read once
      // rendered this page empty while the life map showed the same items.
      const [rows, board] = await Promise.all([
        habitService.getHabits(true),
        habitService.getBoard(day).then((b) => { setBoardIssue(false); return b }).catch(() => { setBoardIssue(true); return null }),
      ])
      setHabits(Array.isArray(rows) ? rows : [])
      setCards(board?.habits || {})
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load habits')
    } finally {
      setLoading(false)
    }
  }, [habitService])

  useEffect(() => { void load() }, [load])

  const visible = useMemo(() => habits.filter((h) => matchesQuery(h, query)), [habits, query])
  const groups = useMemo(() => groupHabits(visible, cards, groupBy, today, lifeAreaLabel, { by: sortBy, dir: sortDir }), [visible, cards, groupBy, today, sortBy, sortDir])
  const summary = useMemo(() => summarize(habits, cards, today), [habits, cards, today])

  // ── verbs ──
  const mark = async (habit: Habit, outcome: 'done' | 'skipped' | 'pending') => {
    try {
      const r = await habitService.markToday(habit.id, outcome, todayKey())
      if (r.board) setCards((c) => ({ ...c, [String(habit.id)]: r.board || undefined }))
      if (outcome === 'done') toast.toast({ variant: 'success', title: 'Done today', description: habit.title, action: { label: 'Undo', onClick: () => void mark(habit, 'pending') } })
      else if (outcome === 'skipped') toast.toast({ variant: 'info', title: 'Not today', description: 'That is allowed. It does not break your run.', action: { label: 'Undo', onClick: () => void mark(habit, 'pending') } })
    } catch (err) {
      toast.error('Could not record that', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    const target = deleteTarget
    try {
      const result = await habitService.deleteHabit(String(target.id))
      setDeleteTarget(null)
      await load()
      toast.toast({ variant: 'success', title: 'Habit deleted', description: describeTaskCleanup(result.cleanup) || undefined })
    } catch (err) {
      setDeleteTarget(null)
      toast.error('Could not delete it', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const quickAdd = async (title: string) => {
    const created = await habitService.createHabit({ title, priority: 'medium' })
    await load()
    toast.toast({ variant: 'success', title: 'Added', description: title, action: created?.id ? { label: 'Set a schedule', onClick: () => navigate(`/habits/${created.id}`) } : undefined })
  }

  const toggle = (key: string, def: boolean) => setCollapsed((c) => ({ ...c, [key]: !(c[key] ?? def) }))

  // ── frame ──
  if (loading) return <div className="space-y-4" aria-busy="true" aria-label="Loading habits"><SkeletonList count={5} /></div>
  if (error && habits.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center" role="alert">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-bad" />
          <h2 className="mb-2 text-xl font-semibold text-primary">The list would not load</h2>
          <p className="mb-4 text-secondary">{error}</p>
          <Button onClick={() => { setLoading(true); void load() }}><RefreshCw className="mr-2 h-4 w-4" />Try again</Button>
        </div>
      </div>
    )
  }

  const empty = habits.length === 0
  const nothingMatches = !empty && visible.length === 0

  return (
    <div className="space-y-4" data-testid="habits-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand sm:h-10 sm:w-10" aria-hidden="true" data-testid="habits-logo">
            <Repeat className="h-5 w-5 text-on-brand" />
          </span>
          <div className="min-w-0">
            <h1 className={`text-xl font-semibold leading-tight text-primary sm:text-2xl ${diaryMode ? 'font-handwritten' : ''}`}>Habits</h1>
            <p className="text-app-meta text-secondary" data-testid="habits-summary">{summaryWords(summary)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="xs" onClick={() => navigate('/habits/create')} data-testid="habits-new"><Plus className="mr-1 h-4 w-4" />New habit</Button>
          <Button size="xs" variant="outline" onClick={() => navigate('/horton-ai?from=habits&type=habit')} data-testid="habits-ask"><Sparkles className="mr-1 h-4 w-4" />{personaName}</Button>
        </div>
      </div>

      {boardIssue && !empty && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-tint-bad-edge bg-tint-bad-soft p-3 text-bad" role="alert">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="text-app-meta">We could not load today's marks and runs, so the rows show the schedule only.</span>
          <Button onClick={() => void load()} variant="outline" size="xs" className="ml-auto shrink-0"><RefreshCw className="mr-1.5 h-4 w-4" />Try again</Button>
        </div>
      )}

      {!empty && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="relative block w-full sm:w-64 lg:w-80">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a habit" aria-label="Find a habit"
                   className="min-h-touch w-full rounded-md border border-soft bg-surface pl-8 pr-8 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30 lg:min-h-0 lg:py-1.5" data-testid="habits-search" />
            {query && <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted hover:text-primary"><X className="h-3.5 w-3.5" /></button>}
          </label>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-app-micro">
            <div className="flex items-center gap-1.5">
              <span className="text-muted">Group by</span>
              <div role="radiogroup" aria-label="Group by" className="inline-flex rounded-md border border-soft bg-surface p-0.5">
                {([['when', 'When'], ['goal', 'Goal'], ['area', 'Life area'], ['none', 'None']] as Array<[HabitGroupBy, string]>).map(([k, label]) => (
                  <button key={k} type="button" role="radio" aria-checked={groupBy === k} onClick={() => setGroupBy(k)} className={`rounded px-2 py-1 font-medium transition-colors ${groupBy === k ? 'bg-brand-subtle text-brand' : 'text-secondary hover:text-primary'}`} data-testid={`habits-group-${k}`}>{label}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-muted">Sort</span>
              <div role="radiogroup" aria-label="Sort" className="inline-flex rounded-md border border-soft bg-surface p-0.5">
                {([['attention', 'Attention'], ['streak', 'Run'], ['created', 'Created'], ['updated', 'Updated']] as Array<[HabitSortBy, string]>).map(([k, label]) => (
                  <button key={k} type="button" role="radio" aria-checked={sortBy === k} onClick={() => setSortBy(k)} className={`rounded px-2 py-1 font-medium transition-colors ${sortBy === k ? 'bg-brand-subtle text-brand' : 'text-secondary hover:text-primary'}`} data-testid={`habits-sort-${k}`}>{label}</button>
                ))}
              </div>
              {sortBy !== 'attention' && (
                <button type="button" onClick={() => setSortDir(sortDir === 'desc' ? 'asc' : 'desc')} aria-label={sortDir === 'desc' ? 'Highest first. Switch to lowest first' : 'Lowest first. Switch to highest first'}
                        className="inline-flex items-center gap-0.5 rounded-md border border-soft bg-surface px-1.5 py-1 font-medium text-secondary hover:text-primary" data-testid="habits-sort-dir">
                  {sortDir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
                  {sortBy === 'streak' ? (sortDir === 'desc' ? 'Longest' : 'Shortest') : (sortDir === 'desc' ? 'Newest' : 'Oldest')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <TaskQuickAdd onAdd={quickAdd} onMore={(title) => navigate('/habits/create', { state: title ? { title } : undefined })} autoFocus={empty} placeholder="Add a habit — a name is enough" />

      {empty && (
        <div className="rounded-xl border border-soft bg-surface p-6 text-center" data-testid="habits-empty">
          <p className="text-app-body font-semibold text-primary">Nothing here yet</p>
          <p className="mx-auto mt-1 max-w-md text-app-meta text-secondary">
            Put one thing you want to do regularly in the box above — a name is enough. Two minutes counts; steady beats big.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button size="xs" variant="outline" onClick={() => navigate('/horton-ai?from=habits&type=habit')}><Sparkles className="mr-1 h-4 w-4" />Build with {personaName}</Button>
          </div>
        </div>
      )}

      {nothingMatches && (
        <p className="text-app-meta text-muted" data-testid="habits-no-match">Nothing matches “{query}”. <button type="button" onClick={() => setQuery('')} className="text-brand hover:underline">Clear the search</button></p>
      )}

      {!empty && !nothingMatches && groups.map((g) => {
        const isCollapsed = collapsed[g.key] ?? Boolean(g.collapsedByDefault)
        const Chevron = isCollapsed ? ChevronRight : ChevronDown
        return (
          <section key={g.key} data-testid={`habits-section-${g.key}`} aria-labelledby={`habits-h-${g.key}`}>
            <button type="button" onClick={() => toggle(g.key, Boolean(g.collapsedByDefault))} aria-expanded={!isCollapsed} className="flex w-full items-center gap-1.5 rounded py-1 text-left hover:text-brand" data-testid="habits-section-toggle">
              <Chevron className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden="true" />
              <h2 id={`habits-h-${g.key}`} className="text-app-meta font-bold uppercase tracking-widest text-secondary">{g.title}</h2>
              <span className="text-app-micro text-muted" data-testid="habits-section-count">{g.items.length}</span>
            </button>
            {!isCollapsed && g.items.length > 0 && (
              <div className="mt-1 space-y-1.5">
                {g.items.map((h) => (
                  <HabitRow key={h.id} habit={h} card={cards[String(h.id)]} showParent={groupBy !== 'goal'} showArea={groupBy === 'goal'}
                            timeHint={sortBy === 'created' || sortBy === 'updated' ? sortBy : null} onMark={mark} onDelete={setDeleteTarget} diaryMode={diaryMode} />
                ))}
              </div>
            )}
            {!isCollapsed && g.items.length === 0 && g.quiet && <p className="mt-0.5 pl-5 text-app-meta text-muted" data-testid="habits-section-quiet">{g.quiet}</p>}
          </section>
        )
      })}

      <ConfirmationModal isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} onConfirm={confirmDelete} title="Delete this habit?"
                         message={`“${deleteTarget?.title}” will be removed, along with its schedule and anything set to remind you about it. Archiving from the habit page keeps the history instead.`} variant="error" confirmText="Delete" />
    </div>
  )
}

export default Habits
